








































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";

import {
  getIresDeviceCategoryFromSerialNumber,
  IresDevice,
  IresDeviceCategory,
  isActiveProgramChainOfIresDevice,
} from "@/data/models/IresDevice";

import { ProgramChain } from "@/data/models/ProgramChain";
import {
  formatDryingQualityRating,
  formatOverallDryingRating,
  formatProgramQualityRating,
  formatOverallQualityNotes,
  formatDryingLabels,
  ProgramChainQualityRating,
  DryingLabel,
} from "@/data/models/ProgramChainQualityRating";

import { PanelConfig } from "@/data/models/PanelConfig";
import { ProjectAssignmentDetails } from "@/data/models/ProjectAssignmentInfo";
import { DryingInfoCalculator, SensorInfo } from "@/utils/DryingInfoCalculator";
import { PowerConsumptionValues } from "@/utils/DryingProgressGraphDataExtractor";
import { getClientId } from "@/data/repository/ClientRepository";
import RateProgramQualityComponent from "@/components/devices/ratedrying/RateProgramQuality.vue";
import RateDryingQualityComponent from "@/components/devices/ratedrying/RateDryingQuality.vue";
import DryingProgressGraphWrapperComponent from "../panel/DryingProgressGraphWrapper.vue";
import RatingOverviewComponent from "./RatingOverview.vue";
import { joinToString } from "@/utils/LocalisationHelper";
import PhaseQualityOverviewComponent from "./ratedrying/PhaseQualityOverview.vue";
import DryingInfoTableComponent, {
  DryingInfoTable,
  DryingInfoTableRow,
} from "./DryingInfoTable.vue";
import { formatDateShort } from "@/utils/DateFormatter";
import { isRoomDryingProgram, isTargetDryingProgram } from "@/data/models/ComBox";
import { showProgramQualityRatingAccess, viewIntensityAccess } from "@/permissions/UserPermissions";
import { setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import { ClientModule } from "@/store/modules/ClientModule";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "rate-program-quality": RateProgramQualityComponent,
    "rate-drying-quality": RateDryingQualityComponent,
    "drying-progress": DryingProgressGraphWrapperComponent,
    "rating-overview": RatingOverviewComponent,
    "phase-qualitity-overview": PhaseQualityOverviewComponent,
    "drying-info-table": DryingInfoTableComponent,
  },
})
export default class DryingInfoItemComponent extends Vue {
  @Prop() iresDevice!: IresDevice;
  @Prop() programChain!: ProgramChain;
  @Prop() powerConsumptionValues!: PowerConsumptionValues | null;

  @Prop() projectAssignmentDetails!: ProjectAssignmentDetails | null;
  @Prop({ default: false }) showRelativeChainNumber!: boolean;
  @Prop({ default: true }) showDivider!: boolean;
  @Prop({ default: false }) showDryingGraph!: boolean;
  @Prop() runAssignmentInfo?: string;

  @Prop({ default: () => new Map<number, number>() })
  programChainNumberLookup!: Map<number, number>;
  @Prop() elevation!: number;

  @Prop() config!: PanelConfig | null;

  @Prop({ default: true }) showSensorInfo!: boolean;
  @Prop({ default: false }) enableOpenDetails!: boolean;

  @Prop({ default: false }) showPhaseQualityInfo!: boolean;
  @Prop({ default: false }) showRating!: boolean;
  @Prop({ default: false }) expandRatingDescription!: boolean;
  @Prop({ default: true }) preferAbsHumidity!: boolean;
  
  @Prop({ default: false })
  highlightEntry!: boolean;

  @Prop({ default: "" })
  backgroundStyle!: string;

  @Prop({ default: false })
  forceUpdateBackgroundColor!: boolean;

  @Watch("ignoreIresAdminOrIresClientCheck")
  onIgnoreIresAdminOrIresClientCheckChanged() {
    this.didCalculateSensorInfo = false;
    this.calculateSensorInfoIfNeccessary();
  }

  get ignoreIresAdminOrIresClientCheck() {
    return ClientModule.ignoreIresAdminOrIresClientCheck;
  }

  shouldHighlightProject = true;

  _dryingInfoCalculator: DryingInfoCalculator | null = null;

  phaseLabels: string[] = [];
  sensorInfo: SensorInfo[] = [];
  dryingDays: string[] = [];

  didCalculateSensorInfo = false;
  isCreatingDryingReport = false;

  isComponentVisible = false;

  onOpenProjectDetails() {
    const projectId = 
      this.projectAssignmentDetails?.projectId ?? 
      this.programChain.assignedProjectId ?? 
      this.programChain.assignedProjectDetails?.projectId;
    
    this.$emit("openProjectDetails", projectId, this.programChain.id);

    setLastVisitedItem(
      this.programChain.id,
      StorageKeyLastVisitedItemLevel.LEVEL_4, 
    );
  }

  onOpenProgramChainDetails() {
    this.$emit("openProgramChainDetails", this.programChain);

    setLastVisitedItem(
      this.programChain.id,
      StorageKeyLastVisitedItemLevel.LEVEL_4, 
    );
  }

  get hasPhaseQualityInfo() {
    return (
      getIresDeviceCategoryFromSerialNumber(this.serialNumber) ===
      IresDeviceCategory.COMBOX
    );
  }

  get enableOpenProjectDetails() {
    const clientId = getClientId();

    if (
      this.projectAssignmentDetails?.clientId !== clientId &&
      this.programChain.assignedClientId !== clientId
    ) {
      return false;
    }

    return this.projectAssignmentDetails 
      ? true
      : this.programChain.assignedProjectId 
        ? true
        : this.programChain.assignedProjectDetails?.projectId
          ? true
          : false;
  }

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get isAllowedToViewProgramQualityRating() {
    return showProgramQualityRatingAccess(this.activeUser);
  }

  get rating(): ProgramChainQualityRating | undefined {
    if (!this.showRating) {
      return undefined;
    }

    if (!this.isAllowedToViewProgramQualityRating) {
      //Only show ratings for IRES users for now
      return undefined;
    }

    return this.programChain.rating;
  }

  get showProgramQualityRating() {
    return isActiveProgramChainOfIresDevice(this.iresDevice, this.programChain);
  }

  get dryingInfoCalculator(): DryingInfoCalculator {
    const calculator = this._dryingInfoCalculator;
    
    const isRunAssignmentInfoIncluded = !!calculator?.options?.runAssignmentInfo;
    const isConfigIncluded = !!calculator?.options?.config;

    const isRunAssignmentInfoAvailable = !!this.runAssignmentInfo;
    const isConfigAvailable = !!this.config;

    const shouldReloadCalculator = !calculator ||
      isRunAssignmentInfoIncluded !== isRunAssignmentInfoAvailable ||
      isConfigIncluded !== isConfigAvailable;

    if (shouldReloadCalculator) {
      const newCalculator = new DryingInfoCalculator(
        this,
        this.iresDevice,
        this.programChain,
        this.showRelativeChainNumber,
        {
          runAssignmentInfo: this.runAssignmentInfo,
          config: this.config ?? undefined,
        }
      );

      this._dryingInfoCalculator = newCalculator;

      return newCalculator;
    }

    calculator.programChain = this.programChain;

    return calculator;
  }

  get serialNumber() {
    return this.iresDevice.serialNo;
  }

  get showSensorDescription() {
    return this.dryingInfoCalculator.showSensorDescription;
  }

  get latestValueHeader() {
    return this.dryingInfoCalculator.latestValueHeader;
  }

  get hasSensorInfo() {
    return this.sensorInfo.length > 0;
  }

  get firstRun() {
    return this.runs[0];
  }
  get lastRun() {
    return this.runs[this.runs.length - 1];
  }

  get dryingDurationInfo() {
    return this.dryingInfoCalculator.dryingDurationInfo;
  }

  get assignmentDurationInfo() {
    return this.projectAssignmentDetails
      ? this.dryingInfoCalculator.getAssignmentDurationInfo(
          this.projectAssignmentDetails
        )
      : null;
  }

  get programInfo() {
    return this.dryingInfoCalculator.programInfo;
  }

  get meterReadingInfo() {
    return this.dryingInfoCalculator.meterReadingInfo;
  }

  get inventoryNumber() {
    return this.dryingInfoCalculator.inventoryNumber;
  }

  get runs() {
    return this.programChain.runs;
  }

  get assignedProjectDescription(): string | null {
    return this.projectAssignmentDetails
      ? this.dryingInfoCalculator.getAssignedProjectDescription(
          this.projectAssignmentDetails
        )
      : null;
  }

  get deviceDescription() {
    return this.dryingInfoCalculator.deviceDescription;
  }

  get chainNumber() {
    return this.dryingInfoCalculator.getChainNumber(
      this.programChainNumberLookup
    );
  }

  get programNumber() {
    return this.programChain.runs[0].programNumber ?? 1;
  }

  isRoomDryingProgram() {
    return isRoomDryingProgram(this.programNumber);
  }

  isTargetDryingProgram() {
    return isTargetDryingProgram(this.programNumber);
  }

  get isAllowedToViewIntensity() {
    return viewIntensityAccess(this.activeUser);
  }

  calculateSensorInfoIfNeccessary() {
    if (!this.showSensorInfo) {
      return;
    }

    if (this.didCalculateSensorInfo) {
      return;
    }

    this.didCalculateSensorInfo = true;

    const {
      sensorInfo,
      dryingPhaseLabels,
      dryingDays,
    } = this.dryingInfoCalculator.calculateSensorInfo(
      this.isAllowedToViewIntensity,
      this.preferAbsHumidity
    );

    this.sensorInfo = sensorInfo;
    this.phaseLabels = dryingPhaseLabels;
    this.dryingDays = dryingDays;
  }

  visibilityChanged(isVisible: boolean) {
    this.isComponentVisible = isVisible;

    if (isVisible) {
      this.calculateSensorInfoIfNeccessary();
    }
  }

  get ratingNote() {
    const note = this.rating?.note;

    if (!note) {
      return null;
    }

    return note;
  }

  get overallDryingRating() {
    let rating = this.rating?.overallDryingRating;

    if (
      this.rating &&
      this.rating.dryingDurationRating &&
      this.rating.dryingDurationRating.shouldHaveRunLonger &&
      this.rating.overallDryingRating?.dryingDuration === undefined
    ) {
      if (rating) {
        rating.dryingDuration = -2;
      }
      else {
        rating = {
          dryingDuration: -2,
          dryingProcess: null,
          knowledgeGain: null
        }
      }
    }

    if (rating) {
      if (rating.dryingProcess === 0) {
        rating.dryingProcess = null;
      }
      if (rating.knowledgeGain === 0) {
        rating.knowledgeGain = null;
      }
    }

    if (!rating) {
      return null;
    }

    return formatOverallDryingRating(this, rating);
  }

  get overallQualityNotes() {
    const notes = this.rating?.notesDryingRating ?? {
      anomalies: [],
      general: [],
      roomConditions: [],
      roomTemperature: [],
      EDTGeneral: [],
      EDTPhase4: [],
      EDTPauses: [],
    };

    if (
      this.rating &&
      this.rating.notesDryingRating === undefined && 
      this.rating.labels.length > 0
    ) {
      const labels = this.rating.labels;

      if (labels.includes(DryingLabel.NO_PLASTIC_SHEETING_ON_RAW_FLOOR)) {
        notes["EDTGeneral"].push(0);
      }
      if (labels.includes(DryingLabel.UNDERFLOOR_HEATING)) {
        notes["general"].push(0);
      }
      if (labels.includes(DryingLabel.NEW_MOISTURE_APPEARED)) {
        notes["anomalies"].push(1);
      }
      if (labels.includes(DryingLabel.NIGHTLY_INTERRUPTION_BY_CLIENT)) {
        notes["general"].push(1);
      }
      if (labels.includes(DryingLabel.INSUFFICIENT_AIR_FLOW)) {
        notes["EDTGeneral"].push(1);
      }
      if (labels.includes(DryingLabel.RELAY_MALFUNCTION)) {
        notes["anomalies"].push(0);
      }
      if (labels.includes(DryingLabel.MOISTURE_NEAR_DRILL_HOLES)) {
        notes["EDTGeneral"].push(2);
      }
      if (labels.includes(DryingLabel.DRYING_WITH_PRE_DRIED_AIR)) {
        notes["EDTGeneral"].push(3);
      }
    }

    if (!notes) {
      return null;
    }

    return formatOverallQualityNotes(this, notes);
  }

  get programQualityRating() {
    const rating = this.rating?.programQualityRating;
    const programNumber = this.programChain.runs[0].programNumber ?? 1;

    if (!rating) {
      return null;
    }

    return formatProgramQualityRating(this, rating, programNumber);
  }

  get dryingQualityRating() {
    const rating = this.rating?.dryingQualityRating;

    if (!rating) {
      return null;
    }

    return formatDryingQualityRating(this, rating);
  }

  get compactOverallRatingDescription() {
    const rating = this.rating?.overallDryingRating;

    if (!rating) {
      return null;
    }

    const result = formatOverallDryingRating(this, rating);

    if (!result) {
      return null;
    }

    return joinToString(
      result.map((info) => `${info.label}: ${info.rating}`),
      ", "
    );
  }

  get dryingLabels() {
    const labels = this.rating?.labels;

    if (!labels) {
      return null;
    }

    return formatDryingLabels(this, labels);
  }

  get dryingDurationRating() {
    const dryingDurationRating = this.rating?.dryingDurationRating;

    if (!dryingDurationRating) {
      return null;
    }

    const { shouldHaveRunLonger, shouldHaveStoppedAt } = dryingDurationRating;

    if (shouldHaveRunLonger) {
      return this.$t(
        "rateDrying.dryingDuration.shouldHaveRunLonger"
      ).toString();
    }

    if (shouldHaveStoppedAt) {
      return this.$t("rateDrying.dryingDuration.formatShouldHaveStoppedAt", [
        formatDateShort(shouldHaveStoppedAt),
      ]).toString();
    }

    return null;
  }

  /**
   * Phones and iPad mini (below 960 px) show a history entry as a summary beside its thumbnail; on
   * an iPad mini the label column wrapped most values onto a second line (Taimoor, 2026-10-06).
   */
  get isPhoneHistoryEntry() {
    return this.$vuetify.breakpoint.smAndDown && this.showDryingGraph;
  }

  /**
   * The general info rows as the phone summary's grey lines: Zuordnung, Trocknung and Qualität keep
   * their label, the meter reading ("Anfang …, Ende …") and the program name read on their own.
   */
  get phoneHistoryLines(): { label: string | null; value: string }[] {
    const lines = [
      { label: this.$t("dryingInfo.devicesAssinged").toString(), value: this.assignmentDurationInfo },
      { label: this.$t("dryingInfo.drying").toString(), value: this.dryingDurationInfo },
      { label: null, value: this.meterReadingInfo },
      { label: null, value: this.programInfo },
      {
        label: this.$t("rateDrying.quality").toString(),
        value: this.expandRatingDescription ? null : this.compactOverallRatingDescription,
      },
    ];

    return lines.filter((line): line is { label: string | null; value: string } => !!line.value);
  }

  get programChainGeneralInfoTable(): DryingInfoTable {
    const rows: DryingInfoTableRow[] = [];

    const addRow = (label: string, value?: string | null, bold?: boolean, highlight?: boolean) => {
      if (value || value === "") {
        rows.push({
          bold: bold,
          allowVariableValuesWidth: true,
          rowLabels: [label],
          rowValues: [value],
          highlight: highlight ? [highlight] : undefined
        });
      }
    };

    addRow(
      this.chainNumber.toString(),
      this.assignedProjectDescription ?? "",
      true,
      this.enableOpenProjectDetails ?? undefined
    );

    addRow(
      this.$t("dryingInfo.devicesAssinged").toString(),
      this.assignmentDurationInfo
    );

    addRow(this.$t("dryingInfo.drying").toString(), this.dryingDurationInfo);

    addRow(
      this.$t("panelDetail.meterReading").toString(),
      this.meterReadingInfo
    );

    addRow(this.$t("dryingInfo.program").toString(), this.programInfo);

    if (!this.expandRatingDescription) {
      addRow(
        this.$t("rateDrying.quality").toString(),
        this.compactOverallRatingDescription
      );
    }

    // We now have separate sections for these
    /* addRow(
      this.$t("rateDrying.qualityOverall.exemplaryFor").toString(),
      this.dryingLabels
    ); */

    /* addRow(
      this.$t("rateDrying.dryingDuration.title").toString(),
      this.dryingDurationRating
    ); */

    return {
      rows: rows,
    };
  }

  get sensorInfoTable(): DryingInfoTable {
    const rows: DryingInfoTableRow[] = [];

    const headerRow = {
      bold: true,
      rowLabels: [this.$t("dryingInfo.measurements").toString()],
      rowValues: [
        this.$t("dryingInfo.startValue").toString(),
        ...this.phaseLabels,
        this.latestValueHeader,
        this.$t("dryingInfo.minValue").toString(),
        this.$t("dryingInfo.maxValue").toString(),
      ],
    };

    rows.push(headerRow);

    const dryingDaysRow = {
      rowLabels: [this.$t("dryingInfo.day").toString()],
      rowValues: this.dryingDays,
    };

    rows.push(dryingDaysRow);

    this.sensorInfo.forEach((info) => {
      const rowLabels = this.showSensorDescription
        ? [info.sensorDescription ?? "", info.label]
        : [info.label];

      rows.push({
        rowLabels: rowLabels,
        rowValues: [
          info.startValue,
          ...info.phaseChangeInfo,
          info.latestValue,
          info.minValue,
          info.maxValue,
        ],
      });

      // Material (Widerstand) sensors have a single resistance row, so their
      // usage tag ("(MESS)") is rendered as an extra stacked label-only row
      // beneath the sensor name, mirroring regular sensors' name/usage stack.
      if (info.usageDescription && this.showSensorDescription) {
        rows.push({
          rowLabels: [info.usageDescription, ""],
          rowValues: [],
        });
      }
    });

    return {
      rows: rows,
    };
  }

  onDryingProgressMouseEnter() {
    this.shouldHighlightProject = false;
  }

  onDryingProgressMouseLeave() {
    this.shouldHighlightProject = true;
  }
}
